import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    PropsWithChildren, useRef, RefObject
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { registerOnForcedLogout } from '@app/lib/api';
import { User as AuthenticatedUser } from '@app/types';
import { hasPermission as hasRequiredPermission, PermissionRequirement } from '@modules/auth/lib/permission.shared';
import { useCreateLocalLoading, Loading } from "react-easy-loading";
import i18next from 'i18next';

type LoginDto = {
    email: string;
    password: string;
}

interface AuthContextType {
    isAuthenticated: boolean;
    user: AuthenticatedUser | null;
    login: (credentials: LoginDto) => Promise<void>;
    logout: () => void;
    loading: Loading;
    isLoggingIn: RefObject<boolean>;
    authenticateBrowser: (accessToken: string, refreshToken: string, user: unknown) => void;
    permissions: string[];
    hasPermission: (required: PermissionRequirement) => boolean;
    serverRetryWarning: string | null;
}

//TODO: make the initial login query retry.
const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ME_RETRY_MAX = 5;
const ME_RETRY_BASE_DELAY = 1500;
const DEV_LOGGED_OUT_KEY = 'devLoggedOut';
const DEV_SUPER_ADMIN_CREDENTIALS: LoginDto = {
    email: 'admin@balancia.local',
    password: 'Admin123!',
};
const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

const isNetworkOrServerError = (result: { error?: unknown }, error?: unknown): boolean => {
    // Network errors thrown by fetch (server down, ECONNREFUSED, etc.)
    if (error instanceof TypeError) return true;
    // HTTP 5xx from the server
    const status: number =
        (result as any).response?.status ??
        (result.error as any)?.statusCode ??
        0;
    return status >= 500;
};

export const AuthProvider = ({ children }: PropsWithChildren) => {
    const [user, setUser] = useState<AuthenticatedUser | null>(null);
    const [serverRetryWarning, setServerRetryWarning] = useState<string | null>(null);
    const isLoggingIn = useRef(false);
    const loading = useCreateLocalLoading();
    const navigate = useNavigate();
    const location = useLocation();

    // Keep a ref to the latest location so long-lived callbacks (forced logout,
    // storage events) don't capture a stale value or trigger re-registration.
    const locationRef = useRef(location);
    useEffect(() => { locationRef.current = location; }, [location]);

    const goToAuth = useCallback(() => {
        const current = locationRef.current;
        if (current.pathname === '/auth') return;
        navigate('/auth', { state: { from: current }, replace: true });
    }, [navigate]);

    const authenticateBrowser = useCallback((accessToken: string, refreshToken: string, user: unknown) => {
        localStorage.removeItem(DEV_LOGGED_OUT_KEY);
        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        setUser(user as AuthenticatedUser);
    }, [setUser]);

    useEffect(() => {
        let cancelled = false;

        const initAuth = async () => {
            const token = localStorage.getItem('token');
            if (token) {
                let attempt = 0;

                while (!cancelled) {
                    let fetchError: unknown = undefined;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    let result: any = null;

                    try {
                        result = await api.GET("/v1/api/auth/me");
                    } catch (err) {
                        fetchError = err;
                    }

                    if (cancelled)
                        break;

                    // Success
                    if (result?.data) {
                        setUser(result.data as AuthenticatedUser);
                        setServerRetryWarning(null);
                        break;
                    }

                    // Transient error: server 5xx or network failure → retry with backoff
                    if (isNetworkOrServerError(result ?? {}, fetchError) && attempt < ME_RETRY_MAX) {
                        attempt++;
                        const delay = Math.min(ME_RETRY_BASE_DELAY * Math.pow(2, attempt - 1), 30_000);
                        setServerRetryWarning(i18next.t('auth.ui.retrying', {attempt, max: ME_RETRY_MAX}));
                        await sleep(delay);
                        continue;
                    }

                    // Permanent auth failure — the api middleware has already wiped tokens
                    // and invoked the forced-logout callback, so just stop retrying here.
                    break;
                }
            } else if (import.meta.env.DEV && localStorage.getItem(DEV_LOGGED_OUT_KEY) !== 'true') {
                try {
                    const response = await api.POST("/v1/api/auth/login", {
                        body: DEV_SUPER_ADMIN_CREDENTIALS,
                    });

                    if (!cancelled && response.data) {
                        const { accessToken, refreshToken, user } = response.data;
                        authenticateBrowser(accessToken, refreshToken, user);
                    }
                } catch (error) {
                    console.error('Dev auto-login failed:', error);
                }
            }

            if (!cancelled) {
                setServerRetryWarning(null);
                loading.set('success');
            }
        };

        void initAuth();

        return () => {
            cancelled = true;
        };
    }, [authenticateBrowser]);

    // Forced logout from the api middleware (refresh token expired/invalid).
    // The middleware has already cleared localStorage by the time we run.
    useEffect(() => {
        return registerOnForcedLogout(() => {
            setUser(null);
            goToAuth();
        });
    }, [goToAuth]);

    // Cross-tab logout sync: another tab cleared the token → mirror the state here.
    useEffect(() => {
        const handler = (e: StorageEvent) => {
            if (e.storageArea !== localStorage) return;
            if (e.key !== 'token') return;
            if (e.newValue === null) {
                setUser(null);
                goToAuth();
            }
        };
        window.addEventListener('storage', handler);
        return () => window.removeEventListener('storage', handler);
    }, [goToAuth]);

    const permissions = useMemo(() => user?.permissions ?? [], [user?.permissions]);
    const hasPermission = useCallback((required: PermissionRequirement) => {
        if (!user) return false;
        return hasRequiredPermission({ ...user, permissions }, required);
    }, [user, permissions]);

    const login = useCallback(async (credentials: LoginDto) => {
        isLoggingIn.current = true;
        try {
            const response = await api.POST("/v1/api/auth/login", {
                body: credentials
            });
            if (response.data) {
                const { accessToken, refreshToken, user } = response.data;
                authenticateBrowser(accessToken, refreshToken, user);
                const from = location.state?.from?.pathname || '/dashboard';
                navigate(from, { replace: true });
            }
            else {
                throw new Error(i18next.t('auth.failed'));
            }
        } catch (error) {
            console.error('Login failed:', error);
            throw error;
        }
    }, [navigate, authenticateBrowser, location.state?.from?.pathname]);

    const logout = useCallback(async () => {
        // Call the backend first while the refresh token is still present so it
        // actually gets revoked server-side. /auth/logout is public, so no Bearer
        // dance is needed and there's no risk of a mid-call refresh rotation.
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
            try {
                await api.POST("/v1/api/auth/logout", {
                    body: { refreshToken }
                });
            } catch (e) {
                console.error("Logout failed on backend", e);
            }
        }

        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        if (import.meta.env.DEV) {
            localStorage.setItem(DEV_LOGGED_OUT_KEY, 'true');
        }
        setUser(null);
        navigate('/auth');
    }, [navigate, setUser]);

    const contextValue = useMemo(() => ({
        isAuthenticated: !!user,
        user,
        login,
        logout,
        loading,
        isLoggingIn,
        authenticateBrowser,
        permissions,
        hasPermission,
        serverRetryWarning
    }), [user, login, logout, loading, isLoggingIn, authenticateBrowser, permissions, hasPermission, serverRetryWarning]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

/**
 * Non-throwing variant. Returns `undefined` when the caller renders outside
 * an AuthProvider (e.g. the tablet PWA, which authenticates as a device via
 * MQTT and never mounts the user-auth stack). Consumers must handle the
 * undefined case explicitly — usually by degrading to "no permission check".
 */
export const useOptionalAuth = () => useContext(AuthContext);

// dev

const byPassAuth = (to = true) => {
    localStorage.setItem('byPassAuth', to ? "true" : "false");
}

if (typeof window !== 'undefined') {
    (window as any).byPassAuth = byPassAuth;
}

const getMockedUser = (): AuthenticatedUser => {
    return {
        id: "mockedId",
        email: "mock@test.com",
        name: "Mocked",
        permissions: []
    }
}
